import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Download, Filter, Search, Calendar, MessageSquare, Mic, X } from 'lucide-react';
import { postConversationMessages } from '../config/api';
import { getStoredAccessToken, getUserDetails, getUserEmailFromProfile } from '@/services/ssoApi';

interface Conversation {
    id: number;
    user_message: string | null;
    bot_response: string | null;
    timestamp: string;
    metadata: any;
    session_id?: string;
    conversation_type?: string;
}

interface Session {
    session_id: string;
    conversation_type: string;
    started_at: string;
    ended_at: string | null;
    total_messages: number;
    metadata: any;
    user_email?: string | null;
}

const ConversationHistoryPage: React.FC = () => {
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [sessions, setSessions] = useState<Session[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [conversationTypeFilter, setConversationTypeFilter] = useState<string>('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [sessionFilter, setSessionFilter] = useState<string>('all');
    
    // UI states
    const [showFilters, setShowFilters] = useState(false);
    const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
    const [needsSignIn, setNeedsSignIn] = useState(false);

    useEffect(() => {
        fetchConversationHistory();
    }, []);

    const fetchConversationHistory = async () => {
        try {
            setLoading(true);
            const token = getStoredAccessToken();
            const details = await getUserDetails(token);
            const rawProfileEmail =
                details && !("error" in details) ? getUserEmailFromProfile(details) : null;
            const userEmail = rawProfileEmail ? rawProfileEmail.trim().toLowerCase() : null;

            if (!userEmail) {
                setNeedsSignIn(true);
                setSessions([]);
                setConversations([]);
                setError(null);
                return;
            }
            setNeedsSignIn(false);

            const messagesResponse = await postConversationMessages({
                user_email: userEmail,
                limit: 500,
            });

            if (!messagesResponse.ok) {
                const errBody = await messagesResponse.json().catch(() => ({}));
                const msg =
                    typeof (errBody as { error?: string }).error === "string"
                        ? (errBody as { error: string }).error
                        : "Failed to fetch conversation history";
                throw new Error(msg);
            }

            const payload = await messagesResponse.json();
            const messages = (payload.messages || []) as Conversation[];

            setConversations(messages);

            const sessionMap = new Map<string, Session>();
            for (const conv of messages) {
                const sid = conv.session_id;
                if (!sid) continue;
                const existing = sessionMap.get(sid);
                if (!existing) {
                    sessionMap.set(sid, {
                        session_id: sid,
                        conversation_type: conv.conversation_type || "text",
                        started_at: conv.timestamp,
                        ended_at: null,
                        total_messages: 1,
                        metadata: {},
                        user_email: userEmail,
                    });
                } else {
                    existing.total_messages += 1;
                }
            }
            setSessions(Array.from(sessionMap.values()));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    const formatTimestamp = (timestamp: string) => {
        const date = new Date(timestamp);
        return date.toLocaleString();
    };

    const formatDate = (timestamp: string) => {
        const date = new Date(timestamp);
        return date.toISOString().split('T')[0];
    };

    // Filter conversations based on current filter states
    const filteredConversations = conversations.filter(conv => {
        // Search term filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const userMessage = conv.user_message?.toLowerCase() || '';
            const botResponse = conv.bot_response?.toLowerCase() || '';
            if (!userMessage.includes(searchLower) && !botResponse.includes(searchLower)) {
                return false;
            }
        }

        // Conversation type filter
        if (conversationTypeFilter !== 'all' && conv.conversation_type !== conversationTypeFilter) {
            return false;
        }

        // Session filter
        if (sessionFilter !== 'all' && conv.session_id !== sessionFilter) {
            return false;
        }

        // Date range filter
        if (dateFrom) {
            const convDate = formatDate(conv.timestamp);
            if (convDate < dateFrom) {
                return false;
            }
        }

        if (dateTo) {
            const convDate = formatDate(conv.timestamp);
            if (convDate > dateTo) {
                return false;
            }
        }

        return true;
    });

    const exportConversations = () => {
        const dataToExport = filteredConversations.map(conv => ({
            id: conv.id,
            timestamp: conv.timestamp,
            conversation_type: conv.conversation_type,
            session_id: conv.session_id,
            user_message: conv.user_message,
            bot_response: conv.bot_response,
            metadata: JSON.stringify(conv.metadata)
        }));

        if (exportFormat === 'csv') {
            // Convert to CSV with proper UTF-8 BOM
            const headers = ['ID', 'Timestamp', 'Type', 'Session ID', 'User Message', 'Bot Response', 'Metadata'];
            const csvContent = [
                headers.join(','),
                ...dataToExport.map(row => [
                    row.id,
                    `"${row.timestamp}"`,
                    `"${row.conversation_type}"`,
                    `"${row.session_id}"`,
                    `"${(row.user_message || '').replace(/"/g, '""')}"`,
                    `"${(row.bot_response || '').replace(/"/g, '""')}"`,
                    `"${row.metadata.replace(/"/g, '""')}"`
                ].join(','))
            ].join('\n');

            // Add UTF-8 BOM for proper encoding
            const BOM = '\uFEFF';
            const csvWithBOM = BOM + csvContent;
            
            const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `conversation_history_${new Date().toISOString().split('T')[0]}.csv`;
            link.click();
        } else {
            // Export as JSON with proper UTF-8 encoding
            const jsonContent = JSON.stringify(dataToExport, null, 2);
            const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `conversation_history_${new Date().toISOString().split('T')[0]}.json`;
            link.click();
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setConversationTypeFilter('all');
        setDateFrom('');
        setDateTo('');
        setSessionFilter('all');
    };

    const getConversationTypeIcon = (type: string) => {
        return type === 'voice' ? <Mic className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />;
    };

    const getConversationTypeColor = (type: string) => {
        return type === 'voice' 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-green-100 text-green-800';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading conversation history...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Card className="p-8 text-center">
                    <div className="text-red-600 mb-4">
                        <X className="w-12 h-12 mx-auto mb-2" />
                        <h2 className="text-xl font-semibold">Error Loading History</h2>
                    </div>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <Button onClick={fetchConversationHistory} className="bg-blue-600 hover:bg-blue-700">
                        Try Again
                    </Button>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen min-w-0 overflow-x-hidden bg-gray-50">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Conversation History</h1>
                    <p className="text-gray-600">
                        Chat logs stored in Azure SQL for your signed-in account (email).
                    </p>
                </div>

                {needsSignIn && (
                    <Card className="mb-6 border-amber-200 bg-amber-50 p-4 text-amber-900">
                        <p className="text-sm">
                            Sign in with SSO so we can load history tied to your work email. No email was found on
                            your profile.
                        </p>
                    </Card>
                )}

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <Card className="p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <Mic className="w-6 h-6 text-blue-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm text-gray-600">Voice Conversations</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {conversations.filter(c => c.conversation_type === 'voice').length}
                                </p>
                            </div>
                        </div>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <MessageSquare className="w-6 h-6 text-green-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm text-gray-600">Text Conversations</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {conversations.filter(c => c.conversation_type === 'text').length}
                                </p>
                            </div>
                        </div>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <Calendar className="w-6 h-6 text-purple-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm text-gray-600">Total Sessions</p>
                                <p className="text-2xl font-bold text-gray-900">{sessions.length}</p>
                            </div>
                        </div>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-orange-100 rounded-lg">
                                <Search className="w-6 h-6 text-orange-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm text-gray-600">Filtered Results</p>
                                <p className="text-2xl font-bold text-gray-900">{filteredConversations.length}</p>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Controls */}
                <Card className="p-6 mb-6 min-w-0 overflow-hidden">
                    <div className="mb-4 flex flex-wrap items-center gap-4">
                        <div className="min-w-0 flex-1 basis-[min(100%,16rem)]">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Search conversations..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full min-w-0 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        <Button
                            onClick={() => setShowFilters(!showFilters)}
                            variant="outline"
                            className="flex items-center gap-2"
                        >
                            <Filter className="w-4 h-4" />
                            Filters
                        </Button>
                        <Button
                            onClick={exportConversations}
                            className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                        >
                            <Download className="w-4 h-4" />
                            Export
                        </Button>
                    </div>

                    {/* Filter Panel */}
                    {showFilters && (
                        <div className="border-t pt-4 mt-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Conversation Type
                                    </label>
                                    <select
                                        value={conversationTypeFilter}
                                        onChange={(e) => setConversationTypeFilter(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="all">All Types</option>
                                        <option value="voice">Voice</option>
                                        <option value="text">Text</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Session
                                    </label>
                                    <select
                                        value={sessionFilter}
                                        onChange={(e) => setSessionFilter(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="all">All Sessions</option>
                                        {sessions.map(session => (
                                            <option key={session.session_id} value={session.session_id}>
                                                {session.conversation_type} - {session.session_id.substring(0, 20)}...
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        From Date
                                    </label>
                                    <input
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        To Date
                                    </label>
                                    <input
                                        type="date"
                                        value={dateTo}
                                        onChange={(e) => setDateTo(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end mt-4">
                                <Button
                                    onClick={clearFilters}
                                    variant="outline"
                                    className="mr-2"
                                >
                                    Clear Filters
                                </Button>
                                <div className="flex items-center gap-2">
                                    <label className="text-sm font-medium text-gray-700">Export Format:</label>
                                    <select
                                        value={exportFormat}
                                        onChange={(e) => setExportFormat(e.target.value as 'csv' | 'json')}
                                        className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="csv">CSV</option>
                                        <option value="json">JSON</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </Card>

                {/* Conversations List */}
                <div className="space-y-4">
                    {filteredConversations.length === 0 ? (
                        <Card className="p-8 text-center">
                            <div className="text-gray-500">
                                <Search className="w-12 h-12 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold mb-2">No conversations found</h3>
                                <p>Try adjusting your filters or search terms.</p>
                            </div>
                        </Card>
                    ) : (
                        filteredConversations.map((conv) => (
                            <Card key={conv.id} className="min-w-0 overflow-hidden p-6">
                                <div className="mb-4 flex min-w-0 flex-col justify-between gap-2 sm:flex-row sm:items-start">
                                    <div className="flex min-w-0 max-w-full items-start gap-3">
                                        <div className={`shrink-0 rounded-full p-2 ${getConversationTypeColor(conv.conversation_type || 'text')}`}>
                                            {getConversationTypeIcon(conv.conversation_type || 'text')}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm text-gray-500">
                                                {formatTimestamp(conv.timestamp)}
                                            </div>
                                            <div className="break-all text-xs text-gray-400">
                                                Session: {conv.session_id?.substring(0, 30)}...
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="min-w-0 space-y-3">
                                    {conv.user_message && (
                                        <div className="min-w-0">
                                            <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-blue-600">
                                                <span className="h-2 w-2 shrink-0 rounded-full bg-blue-600"></span>
                                                You:
                                            </div>
                                            <div className="min-w-0 max-w-full rounded-lg bg-blue-50 p-3">
                                                <p className="max-h-[min(50vh,28rem)] overflow-x-auto overflow-y-auto overscroll-contain whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800 [overflow-wrap:anywhere]">
                                                    {conv.user_message}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {conv.bot_response && (
                                        <div className="min-w-0">
                                            <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-green-600">
                                                <span className="h-2 w-2 shrink-0 rounded-full bg-green-600"></span>
                                                Beluga:
                                            </div>
                                            <div className="min-w-0 max-w-full rounded-lg bg-green-50 p-3">
                                                <p className="max-h-[min(50vh,28rem)] overflow-x-auto overflow-y-auto overscroll-contain whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800 [overflow-wrap:anywhere]">
                                                    {conv.bot_response}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConversationHistoryPage;
